import React from 'react';
import {
  CheckCircle2, XCircle, Loader2, ChevronRight, ChevronLeft, MailX,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatAbs, formatRelative, PAGE_SIZE } from './utils';

const EmailActivityTable = ({
  logs,
  total,
  loading,
  skip,
  setSkip,
  onRowClick,
}) => {
  return (
    <>
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-slate-100 text-base">
            {loading ? 'Loading…' : `${total.toLocaleString()} email${total === 1 ? '' : 's'}`}
          </CardTitle>
          <div className="text-xs text-slate-400">
            Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
            </div>
          )}
          {!loading && logs.length === 0 && (
            <div
              className="text-center py-12 text-slate-400 text-sm"
              data-testid="email-activity-empty"
            >
              <MailX className="h-10 w-10 mx-auto mb-2 text-slate-500" />
              No emails match these filters.
            </div>
          )}
          {!loading && logs.length > 0 && (
            <div className="divide-y divide-[#2A3658]" data-testid="email-activity-list">
              {logs.map((row, idx) => (
                <button
                  type="button"
                  key={`${row.sent_at}-${row.to}-${idx}`}
                  onClick={() => onRowClick(row)}
                  className="w-full text-left px-4 py-3 hover:bg-[#131C3A]/50 transition-colors flex items-center gap-3"
                  data-testid="email-activity-row"
                >
                  <div className="flex-shrink-0">
                    {row.success ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-100 truncate">
                        {row.subject || '(no subject)'}
                      </span>
                      <Badge variant="outline" className="text-[10px] text-slate-400 border-[#2A3658]">
                        {row.category}
                      </Badge>
                      {!row.success && (
                        <Badge className="bg-red-900/40 text-red-300 text-[10px]">
                          status {row.status_code}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">
                      <span className="text-amber-300">{row.to}</span>
                      {' · '}
                      <span title={formatAbs(row.sent_at)}>{formatRelative(row.sent_at)}</span>
                      {row.error && (
                        <span className="text-red-300"> · {row.error.slice(0, 80)}{row.error.length > 80 ? '…' : ''}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between" data-testid="email-activity-pagination">
          <Button
            variant="outline"
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            disabled={skip === 0 || loading}
            className="border-[#2A3658] bg-transparent text-slate-200 hover:bg-[#131C3A]"
            data-testid="email-activity-prev"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <div className="text-sm text-slate-400">
            Page {Math.floor(skip / PAGE_SIZE) + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
          </div>
          <Button
            variant="outline"
            onClick={() => setSkip(skip + PAGE_SIZE)}
            disabled={skip + PAGE_SIZE >= total || loading}
            className="border-[#2A3658] bg-transparent text-slate-200 hover:bg-[#131C3A]"
            data-testid="email-activity-next"
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </>
  );
};

export default EmailActivityTable;
